import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
  useProductState,
  useProductFunctions,
  SubOptionType,
  OptionsType,
  useClickOutside,
  useGlobalLabels,
} from '@sentecacommerce-theme/lib'

import { IconArrowDown, Typography } from '../../../Common'

export const AvailableOptionsSelect = ({
  setImageIndex,
  selectOptionDefaultLabel,
  showOptionError,
}: {
  setImageIndex: React.Dispatch<React.SetStateAction<number | undefined>>
  showOptionError: boolean
  selectOptionDefaultLabel?: string
}) => {
  const [isOpened, setIsOpened] = useState(false)
  const optionsRef = useRef(null)
  const { selectedVariant } = useProductState()
  const masterOption = selectedVariant?.options?.[0] as OptionsType
  const defaultOptionText = `${selectOptionDefaultLabel} ${masterOption?.label}`
  const [selectedOption, setSelectedOption] = useState(defaultOptionText)
  const labels = useGlobalLabels()
  const definitionKey = masterOption?.definitionKey
  const { selectVariantOptions } = useProductFunctions()

  const handleOptionChange = (label: string, optionKey: string) => {
    setSelectedOption(label)
    selectVariantOptions(definitionKey, optionKey)
  }

  useClickOutside(optionsRef, () => setIsOpened(false), [optionsRef])

  useEffect(() => {
    setImageIndex(
      selectedVariant?.mappedSelectedOptions?.[0]?.value?.imageIndex || 0
    )
  }, [selectedVariant, setImageIndex])

  const handleKeyDown = (e: any) => {
    if (e.key === 'Escape') {
      setIsOpened(false)
    }
  }

  return (
    <>
      <MainContainer onClick={() => setIsOpened(!isOpened)} ref={optionsRef}>
        <Container isOpened={isOpened} onKeyDown={handleKeyDown} tabIndex={0}>
          <Select>
            <Typography variant="6">{selectedOption}</Typography>
            <ToggleContainer isOpened={isOpened}>
              <IconArrowDown />
            </ToggleContainer>
          </Select>
        </Container>
        {isOpened ? (
          <Content>
            {selectedVariant?.options?.[0].elements?.map(
              (el: SubOptionType, index) => (
                <Option
                  key={index}
                  onClick={() => handleOptionChange(el.label, el.optionKey)}
                >
                  <Typography variant="7">{el.label}</Typography>
                </Option>
              )
            )}
          </Content>
        ) : null}
        {showOptionError && (
          <ErrorMsg>{labels?.errorMessages?.pleaseSelectAnOption}</ErrorMsg>
        )}
      </MainContainer>
    </>
  )
}

const Container = styled.div.attrs({
  className: 'd-flex align-items-center justify-content-start py-2 px-4',
})<{ isOpened?: boolean }>`
  width: 100%;
  cursor: pointer;
  border: 0.1rem solid ${({ theme }) => theme.palette.grey};
  background-color: ${({ theme }) => theme.palette.white};
  min-height: 4rem;
  border-radius: ${({ isOpened }) =>
    !isOpened ? '0.4rem' : '0.4rem 0.4rem 0 0'};
  border-bottom: ${({ isOpened }) => isOpened && 'none'};

  &:focus {
    outline: none;
  }
`
const MainContainer = styled.div.attrs({
  className: 'mb-3 px-3',
})`
  position: relative;
  color: ${({ theme }) => theme.palette.greyTitle};
`

const Select = styled.span.attrs({
  className: 'd-flex align-items-center justify-content-between',
})`
  width: 100%;
  overflow: hidden;
`

const ToggleContainer = styled.div.attrs({
  className: '',
})<{ isOpened?: boolean }>`
  cursor: pointer;
  & > svg {
    width: 2rem;
    transition: transform 0.25s ease;
    transform: ${(props) =>
      props.isOpened && `translate(0%, 12%) rotate(180deg)`};
  }
`

const Content = styled.div`
  z-index: 2;
  position: absolute;
  width: 100%;
  bottom: auto;
  left: auto;
  background-color: ${({ theme }) => theme.palette.white};
  border: 0.1rem solid ${({ theme }) => theme.palette.grey};
  border-radius: 0 0 0.4rem 0.4rem;
`
const Option = styled.div.attrs({ className: 'py-3 px-4' })`
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  &:last-child {
    border-radius: 0 0 0.4rem 0.4rem;
  }
  &:hover {
    background-color: ${({ theme }) => theme.palette.lightgrey};
  }
`

const ErrorMsg = styled.div.attrs({ className: 'my-2 ml-4' })`
  color: ${({ theme }) => theme.palette.danger};
`
